import { css, ITooltipProps, MessageBar, MessageBarType, Panel, PanelType, Spinner, Stack, Tooltip } from "office-ui-fabric-react";
import * as React from "react";
import { Calendar, View, Event, momentLocalizer, ViewsProps, SlotInfo, Views } from "react-big-calendar";
import moment from "moment";
import { getRoomBookings, getRoomInfo } from "../../../api/rooms";
import { BookingStatus, ConstraintType, IBooking, IBookingConstraint, IRoomAvailability } from "../../../types/Booking.types";

import "react-big-calendar/lib/css/react-big-calendar.css";
import styles from "./CalendarView.module.scss";
import { RoomBookingForm } from "../forms/RoomBookingForm";
import { IRoom } from "../../../types/SiteLocation.types";
import { ProfileContext } from "../../../Home";

export interface ICalendarViewProps {
    fromDate?: Date;
    toDate?: Date
    roomName: string;
    roomId: number;
    workWeekOnly?: boolean;
    onDismiss: () => void;
}

const getDefaultStartDate = (view: View, date: Date) => {
    const newDate = new Date(date);
    if (view === "month" || view === "agenda") {
        return new Date(newDate.getFullYear(), newDate.getMonth(), 1, 0, 0, 0);
    } else if (view === "week" || view === "work_week") {
        while (newDate.getDay() > 0) {
            newDate.setDate(newDate.getDate() - 1);
        }
    } 
    return newDate;
}

const getDefaultEndDate = (view: View, date: Date) => {
    const newDate = new Date(date);
    if (view === "month" || view === "agenda") {
        newDate.setMonth(newDate.getMonth() + 1);
        newDate.setDate(1);
        newDate.setDate(newDate.getDate() - 1);
        return new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), 0, 0, 0);
    } else if (view === "week" || view === "work_week") {
        while (newDate.getDay() < 6) {
            newDate.setDate(newDate.getDate() + 1);
        }
    }
    return newDate;
}

const localizer = momentLocalizer(moment);

export const CalendarView = (props: ICalendarViewProps) => {
    const [ view, setView ] = React.useState<View>("work_week");
    const [ fromDate, setFromDate ] = React.useState<Date>(props.fromDate || getDefaultStartDate(view, new Date()));
    const [ toDate, setToDate ] = React.useState<Date>(props.toDate || getDefaultEndDate(view, new Date()));
    const [ focusedDate, setFocusedDate ] = React.useState<Date>(props.fromDate || new Date());
    const [ error, setError ] = React.useState<string>();
    const [ bookings, setBookings ] = React.useState<IBooking[]>([]);
    const [ loading, setLoading ] = React.useState(false);
    const [ bookRoomId, setBookRoomId ] = React.useState<number>();
    const [ bookingStart, setBookingStart ] = React.useState<Date>();
    const [ bookingEnd, setBookingEnd ] = React.useState<Date>();
    const [ booking, setBooking ] = React.useState<IBooking>();
    const [ room, setRoom ] = React.useState<IRoom>();
    const [ bookingConstraint, setBookingConstraint ] = React.useState<IBookingConstraint>();
    const [ tooltipProps, setTooltipProps ] = React.useState<ITooltipProps>();

    const views: View[] = React.useMemo(() => {
        const v = [ "month", "work_week", "week", "day" ] as View[];
        if (props.workWeekOnly) {
            v.splice(2, 1);
        }
        return v;
    }, [props.workWeekOnly]);

    const _loadBookings = React.useCallback(async (abortController: AbortController) => {
        setLoading(true);
        const roomResponse = await getRoomInfo(props.roomId);
        if (roomResponse.ok) {
            const roomObj = await roomResponse.json() as IRoom;
            if (roomObj) setRoom(roomObj);
            if (roomObj.RoomBookingConstraint) {
                setBookingConstraint(roomObj.RoomBookingConstraint);
            }
        } else {
            setError("Unable to load room details");
            setLoading(false);
            return;
        }
        const bookingsResponse = await getRoomBookings({ roomId: props.roomId, fromDate, toDate }, abortController);
        if (bookingsResponse.ok) {
            setBookings(await bookingsResponse.json() as IBooking[]);
        } else {
            setError("Unable to load bookings");
        }
        setLoading(false);
    }, [ fromDate, toDate, view, props.roomId ]);

    React.useEffect(() => {
        const abortController = new AbortController();
        _loadBookings(abortController);

        return () => {
            abortController.abort();
        }
    }, [ _loadBookings ]);

    const checkForOverlap = React.useCallback((slotStart: Date, slotEnd: Date) => {
        let overlap = false;
        for (let i = 0; i < bookings.length; i++) {
            const booking = bookings[i];
            const bookingStart = new Date(booking.Start);
            const bookingEnd = new Date(booking.Finish);
            if (slotStart < bookingEnd && slotEnd > bookingStart) {
                overlap = true;
                break;
            }   
        }
        return overlap;
    }, [bookings]);

    // If Desk Booking Time Constraint set globally, set maximum date on the date picker
    let maxDate: Date | undefined;
    if (bookingConstraint) {
        const num = bookingConstraint.Number;
        const type = bookingConstraint.Type as ConstraintType;
        if (num) {
            maxDate = new Date();
            switch (type) {
                case ConstraintType.Week:
                    maxDate.setDate(maxDate.getDate() + (7 * num));
                    break;
                case ConstraintType.Month:
                    maxDate.setMonth(maxDate.getMonth() + num);
                    break;
                case ConstraintType.Year:
                    maxDate.setFullYear(maxDate.getFullYear() + num);
                    break;
            }
        }
    }

    // const events = bookings.map((booking: IBooking, idx: number) => {
    //     let title: string;
    //     if (Boolean(booking.Description)) {
    //         title = booking.Description;
    //     } else {
    //         title = Boolean(booking.OnBehalfOfEmail) ? booking.OnBehalfOfEmail : booking.BookedByUserName;
    //     }
    //     if (title.length > 100) {
    //         title = title.substring(0,100) + '...';
    //     }

    //     return {
    //         id: booking.Id,
    //         allDay: false,
    //         start: new Date(booking.Start),
    //         end: new Date(booking.Finish),
    //         title,
    //         className: booking.Status !== undefined && booking.Status === BookingStatus.Pending ? css(styles.bookingItem, styles.pending) : styles.bookingItem,
    //         data: booking
    //     } as Event;
    // });

    const generateAvailabilityEvents = (
        room: IRoom | undefined,
        currentView: View,
        focusedDate: Date,
        fromDate: Date,
        toDate: Date
    ): Event[] => {
        if (!room?.RoomAvailability || room.RoomAvailability.length === 0) {
            return [];
        }

        const availabilityEvents: Event[] = [];
        let slotIndex = 0; // Counter for unique IDs

        room.RoomAvailability.forEach((slot) => {
            const effectiveFrom = new Date(slot.EffectiveFrom);
            const effectiveTo = slot.EffectiveTo ? new Date(slot.EffectiveTo) : null;

            // Check if slot is effective in the current date range
            if (toDate < effectiveFrom || (effectiveTo !== null && fromDate > effectiveTo)) {
                return; // Skip this slot, it's not effective in this range
            }

            // Now generate events based on view type
            if (currentView === "day") {
                // For day view, check if today matches the slot's day of week
                const dayOfWeek = focusedDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
                const slotDay = slot.Day === 7 ? 0 : slot.Day; // Convert 7 (Sunday) to 0

                if (dayOfWeek === slotDay) {
                    availabilityEvents.push(createAvailabilityEvent(
                        slot,
                        focusedDate,
                        slotIndex++,
                        "availability"
                    ));
                }
            } else if (currentView === "work_week" || currentView === "week") {
                // For week view, add event for each matching day in the week
                const weekStart = getDefaultStartDate("week", focusedDate);
                weekStart.setDate(weekStart.getDate() - weekStart.getDay());

                for (let i = 0; i < 7; i++) {
                    const dayOfWeek = (i === 0) ? 7 : i; // Convert 0 (Sunday) to 7
                    if (dayOfWeek === slot.Day) {
                        const eventDate = new Date(weekStart);
                        eventDate.setDate(eventDate.getDate() + i);

                        availabilityEvents.push(createAvailabilityEvent(
                            slot,
                            eventDate,
                            slotIndex++,
                            "availability"
                        ));
                    }
                }
            } else if (currentView === "month") {
                // For month view, add event for each matching day in the month
                const monthStart = getDefaultStartDate("month", focusedDate);
                const monthEnd = new Date(focusedDate.getFullYear(), focusedDate.getMonth() + 1, 0);

                for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
                    const dayOfWeek = d.getDay() === 0 ? 7 : d.getDay(); // Convert 0 to 7 for Sunday

                    if (dayOfWeek === slot.Day) {
                        availabilityEvents.push(createAvailabilityEvent(
                            slot,
                            new Date(d),
                            slotIndex++,
                            "availability"
                        ));
                    }
                }
            }
        });

        return availabilityEvents;
    };

    // Helper function to create an availability event
    const createAvailabilityEvent = (slot: any, baseDate: Date, index: number, type: string): Event => {
        const [startHours, startMinutes] = slot.TimeStart.split(":").map(Number);
        const [endHours, endMinutes] = slot.TimeEnd.split(":").map(Number);

        const start = new Date(baseDate);
        start.setHours(startHours, startMinutes, 0);

        const end = new Date(baseDate);
        end.setHours(endHours, endMinutes, 0);

        return {
            id: `availability-${index}`,
            title: "Unavailable",
            start,
            end,
            allDay: false,
            className: styles.availabilitySlot, // Create a CSS class for this
            data: { type, slot } // Store original slot data if needed
        } as Event;
    };    

    const events: Event[] = [
        ...bookings.map((booking: IBooking, idx: number) => {
            let title: string;
            if (Boolean(booking.Description)) {
                title = booking.Description;
            } else {
                title = Boolean(booking.OnBehalfOfEmail) ? booking.OnBehalfOfEmail : booking.BookedByUserName;
            }
            if (title.length > 100) {
                title = title.substring(0,100) + '...';
            }

            return {
                id: booking.Id,
                allDay: false,
                start: new Date(booking.Start),
                end: new Date(booking.Finish),
                title,
                className: booking.Status !== undefined && booking.Status === BookingStatus.Pending ? css(styles.bookingItem, styles.pending) : styles.bookingItem,
                data: { type: "booking", booking }
            } as Event;
        }),
        ...generateAvailabilityEvents(room, view, focusedDate, fromDate, toDate)
    ]; 

    return (
        <Panel isOpen={true} type={PanelType.large} headerText={`${props.roomName} - Calendar`} onDismiss={props.onDismiss} 
            layerProps={{ eventBubblingEnabled: true }}
        >
            { Boolean(room?.Equipment) && (
                <MessageBar styles={{root: { marginBottom: 20}, innerText: { whiteSpace: "pre-wrap"}}} messageBarType={MessageBarType.warning}>
                    { room?.Equipment }
                </MessageBar>
            )}
            { maxDate !== undefined && (
                <MessageBar styles={{root: { marginBottom: 20}}} messageBarType={MessageBarType.info}>
                    Advance booking restrictions are in effect for this room. You may only book slots before: { maxDate.toLocaleDateString() }
                </MessageBar>
            )}
            <Calendar
                selectable={true}
                startAccessor="start"
                endAccessor="end"
                style={{height: 500}}
                view={view}
                views={views}
                events={events}
                localizer={localizer}
                onView={(newView) => {
                    setView(newView);
                    setFromDate(getDefaultStartDate(newView, focusedDate));
                    setToDate(getDefaultEndDate(newView, focusedDate));
                }}
                onNavigate={(newDate, newView) => {
                    const resolvedView = newView || view;
                    setFocusedDate(newDate);
                    setView(resolvedView);
                    setFromDate(getDefaultStartDate(resolvedView, newDate));
                    setToDate(getDefaultEndDate(resolvedView, newDate));
                }}
                // onView={(newView) => setView(newView)}
                // onNavigate={(newDate, newView, action) => {
                //     setFromDate(getDefaultStartDate(newView, newDate));
                //     setToDate(getDefaultEndDate(newView, newDate));
                //     setView(newView);
                // }}
                onSelectSlot={(slotInfo) => {
                    const stDate = slotInfo.start as Date;
                    const edDate = slotInfo.end as Date;
                    if (view === "month") {
                        stDate.setHours(9); stDate.setMinutes(0); stDate.setSeconds(0);
                        edDate.setDate(stDate.getDate()); edDate.setHours(17); edDate.setMinutes(0); edDate.setSeconds(0);
                    }
                    const overlap = checkForOverlap(stDate, edDate);
                    if (overlap) {
                        if (view === "month") {
                            alert('As meetings are already scheduled for the day chosen, please use the Week or Day view to select a more specific time');
                        } else {
                            alert('This slot clashes with another booking');
                        }
                        return;
                    }
                    const today = new Date();
                    if (stDate < today || edDate < today) {
                        alert('Cannot book a date in the past');
                        return;
                    }
                    if (maxDate && (stDate > maxDate || edDate > maxDate)) {
                        alert(`Bookings to this room are restricted. You may only book slots before: ${maxDate.toLocaleDateString()}`);
                        return;
                    }
                    // booking / request form
                    setBookRoomId(props.roomId);
                    setBookingStart(slotInfo.start as Date);
                    setBookingEnd(slotInfo.end as Date);
                }}
                onSelectEvent={(calendarEvent, e) => {
                    const event = (calendarEvent as any).data as { type: string, booking: IBooking };
                    const booking = event.booking;
                    const target = e.currentTarget as HTMLDivElement;
                    const content = event.type === "booking" ? (
                        <Stack>
                            <div><strong>Booked By:</strong> {booking.BookedByUserName}</div>
                            { Boolean(booking.OnBehalfOfEmail) && <div><strong>On Behalf Of:</strong> {booking.OnBehalfOfEmail}</div> }
                            { Boolean(booking.Description) && <div style={{whiteSpace: "pre-wrap"}}><strong>Description:</strong><br/>{booking.Description}</div> }
                        </Stack>
                    ) : <Stack>
                            <div><strong>Booked By:</strong> Admin</div>
                            <div style={{whiteSpace: "pre-wrap"}}><strong>Description:</strong><br/>This room is blocked out from booking during this time.</div>
                        </Stack>;
                    setTooltipProps({
                        targetElement: target,
                        calloutProps: {
                            onDismiss: () => { setTooltipProps(undefined) }
                        },
                        content, 
                    });
                }}
                onDoubleClickEvent={(calendarEvent, e) => {
                    // edit form
                    const event = (calendarEvent as any).data as { type: string, booking: IBooking };
                    if (event.type === "booking") {
                        const matchingBooking = bookings.find((b) => b.Id === (calendarEvent as any).id);
                        setBookRoomId(props.roomId);
                        if (matchingBooking) {
                            setBooking(matchingBooking);
                            setBookingStart(new Date(matchingBooking.Start));
                            setBookingEnd(new Date(matchingBooking.Finish));
                        }
                    }
                }}
                eventPropGetter={(event: any) => {
                    return { 
                        className: event.className,
                        
                    };
                }}
                min={new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate(), 0, 0, 0)}
                max={new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate(), 23, 45, 0)}
            />
            { tooltipProps !== undefined && (
                <Tooltip {...tooltipProps} />
            )}
            { loading === true && (
                <Spinner label="Loading..." />
            )}
            { bookRoomId !== undefined && (
                <RoomBookingForm
                    roomId={props.roomId}
                    roomInfo={room?.Equipment}
                    booking={booking}
                    start={bookingStart || fromDate}
                    finish={bookingEnd || toDate}
                    onDismiss={(changesMade) => {
                        setBookRoomId(undefined);
                        setBooking(undefined);
                        setBookingStart(undefined);
                        setBookingEnd(undefined);
                        if (changesMade) {
                            const abortController = new AbortController();
                            _loadBookings(abortController);
                        }
                    }}
                />
            )}
            <p>Click and drag a timeslot to make a booking. Click a booking for more information. Double click a booking to view the full booking.</p>
        </Panel>
    )
};

export default CalendarView;